import useMfa from '@/components/PageSpecific/login/mfa/UseMfa';
import { deleteCallback, getCallback } from '@/utils/auth/callbacks';
import {
  checkAndDisplayWarning,
  extractClientMetadata,
  getChallengeResponseError,
  getFriendlyError,
  isExpirationError,
} from '@/utils/auth/helpers';
import {
  confirmCustomChallengeAnswer,
  getCurrentUser,
} from '@/utils/aws/cognito/amplifyAuth';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

export default function useMfaFlow(mfaUser, setMfaUser, setStep, updateSnack) {
  const router = useRouter();
  const [loadingStep, setLoadingStep] = useState(null);
  const hasAutoSentRef = useRef(false); // Track if we've already auto-sent OTP
  const mfaUserRef = useRef(null); // Track mfaUser to detect when it changes

  const mfa = useMfa(mfaUser);

  // Memoize client metadata to avoid recalculating on every render
  // Uses shared extractClientMetadata utility function
  const clientMetadata = useMemo(() => {
    if (!mfa.startChallengeData) return {};
    return extractClientMetadata(mfa.startChallengeData, mfa.selectedMethod);
  }, [mfa.startChallengeData, mfa.selectedMethod]);

  /**
   * Sends OTP for the specified MFA method.
   * This function handles the complete flow: fetching client metadata, sending challenge answer,
   * and updating state. Can be used for both manual method selection and auto-send scenarios.
   *
   * @param {Object} currentMfaUser - Current Cognito user object
   * @param {string} methodToUse - MFA method to use ('email', 'sms', or 'totp')
   * @param {Function} fetchMetadata - Function to fetch client metadata
   * @param {Function} updateMfaUser - State setter for mfaUser
   * @param {Function} updateStep - State setter for step
   * @param {Function} showSnack - Function to update snackbar messages
   * @param {Function} updateLoadingStep - State setter for loading step
   * @param {Function} [updatePendingFlag] - Optional state setter for pending flag
   * @returns {Promise} Promise that resolves when OTP is sent
   */
  const sendOtpForMethod = async ({
    mfaUser: currentMfaUser,
    selectedMethod: methodToUse,
    startMfaChallenge: fetchMetadata,
    setMfaUser: updateMfaUser,
    setStep: updateStep,
    updateSnack: showSnack,
    setLoadingStep: updateLoadingStep,
    setPendingMfaContinue: updatePendingFlag,
  }) => {
    return fetchMetadata()
      .then((result) => {
        if (updatePendingFlag) {
          updatePendingFlag(true);
        }

        const responseData = result.data?.data?.body || result.data?.data || {};
        // Extract client metadata with the specified method using shared utility
        const currentClientMetadata = extractClientMetadata(
          responseData,
          methodToUse
        );

        // clientMetadata.mfaMethod ('sms' | 'email') must be used by the Create Auth Challenge
        // Lambda when sending the OTP. The Lambda must read this value to determine which
        // MFA method to use for OTP delivery.
        return confirmCustomChallengeAnswer(currentMfaUser, 'start', {
          ...currentClientMetadata,
          startChallenge: 'true',
        });
      })
      .then((challengeResponse) => {
        updateMfaUser(challengeResponse);
        updateStep('mfa-otp');
        updateLoadingStep(null); // Clear loading state after OTP is sent
      })
      .catch((err) => {
        updateLoadingStep(null);
        showSnack({ severity: 'error', message: getFriendlyError(err) });
        throw err; // Re-throw so caller can handle
      })
      .finally(() => {
        if (updatePendingFlag) {
          updatePendingFlag(false);
        }
      });
  };

  const handleMfaMethodContinue = async () => {
    setLoadingStep('mfa-method');
    try {
      await sendOtpForMethod({
        mfaUser,
        selectedMethod: mfa.selectedMethod,
        startMfaChallenge: mfa.startMfaChallenge,
        setMfaUser,
        setStep,
        updateSnack,
        setLoadingStep,
      });
    } catch (err) {
      // Error already handled in sendOtpForMethod
      // Just ensure loading state is cleared
      setLoadingStep(null);
    }
  };

  const handleOtpSubmit = async ({ otp }) => {
    setLoadingStep('mfa-otp');
    try {
      // If sendCustomChallengeAnswer throws (e.g., lockout), it will be caught below
      // MFA session cache policy is stored in hub_users via profile PUT; Lambdas should read it
      // there so we do not cache it in browser storage at login.
      const challengeResponse = await confirmCustomChallengeAnswer(
        mfaUser,
        otp
      );
      setMfaUser(challengeResponse);

      // Check for errors in challenge response (we control these)
      const errorData = getChallengeResponseError(challengeResponse);
      if (errorData) {
        // Show warning only when attempt failed
        checkAndDisplayWarning(challengeResponse, updateSnack);
        const error = new Error(errorData.message);
        error.code = errorData.code;
        error.name = errorData.code;
        throw error;
      }

      // Verify session is established before redirecting (fixes mobile redirect issue)
      await getCurrentUser();

      const callbackPath = getCallback();
      deleteCallback();
      if (callbackPath) {
        router.push(callbackPath);
      } else {
        router.push('/hub');
      }
      return { success: true };
    } catch (err) {
      setLoadingStep(null);
      updateSnack({ severity: 'error', message: getFriendlyError(err) });
      // Throw error so Formik's isSubmitting state is properly reset
      throw err;
    }
  };

  const handleResendOtp = async () => {
    setLoadingStep('resend');

    try {
      const challengeResponse = await confirmCustomChallengeAnswer(
        mfaUser,
        'resend',
        {
          ...clientMetadata,
          resendChallenge: 'true',
          mfaMethod: mfa.selectedMethod,
        }
      );
      setMfaUser(challengeResponse);
      updateSnack({
        severity: 'success',
        message: 'A new code has been sent.',
      });
      // Don't show warning after successful resend - warning should only appear after failed verification attempts
    } catch (err) {
      updateSnack({ severity: 'error', message: getFriendlyError(err) });
    }
    setLoadingStep(null);
  };

  // Reset auto-send flag when mfaUser changes to a new user (new MFA flow started)
  useEffect(() => {
    // Only reset if mfaUser actually changed to a different user
    if (!mfaUser) {
      hasAutoSentRef.current = false;
      mfaUserRef.current = null;
    } else if (mfaUserRef.current?.username !== mfaUser.username) {
      // New user, reset the flag
      hasAutoSentRef.current = false;
      mfaUserRef.current = mfaUser;
    }
  }, [mfaUser]);

  // Auto-send OTP when preferred method is available
  // This skips the method selection screen and goes directly to OTP entry
  useEffect(() => {
    // Only auto-send if:
    // 1. mfaUser exists (MFA flow has started)
    // 2. Preferred method exists and is available
    // 3. We haven't already auto-sent for this user
    // 4. We're not currently in a loading state
    // 5. startMfaChallenge is available (needed to fetch metadata)
    // 6. This is a new MFA flow (mfaUser just changed)
    if (
      mfaUser &&
      mfa.hasPreferredMethod &&
      mfa.preferredMethod &&
      mfa.startMfaChallenge &&
      !hasAutoSentRef.current &&
      !loadingStep &&
      mfaUserRef.current?.username === mfaUser.username // Only for the current user
    ) {
      hasAutoSentRef.current = true;
      setLoadingStep('mfa-otp'); // Set loading state for OTP entry

      // Create a silent updateSnack that suppresses expiration errors during auto-send
      const silentUpdateSnack = (alertObj) => {
        // Suppress expiration errors during auto-send - user hasn't done anything yet
        // Show all other messages (errors, warnings, success) except expiration errors
        if (!isExpirationError(alertObj?.message)) {
          updateSnack(alertObj);
        }
        // Suppress expiration errors - they'll be shown when user actually tries to use the code
      };

      // Auto-send OTP using preferred method
      sendOtpForMethod({
        mfaUser,
        selectedMethod: mfa.preferredMethod,
        startMfaChallenge: mfa.startMfaChallenge,
        setMfaUser,
        setStep,
        updateSnack: silentUpdateSnack, // Use silent version to suppress expiration errors
        setLoadingStep,
      }).catch((err) => {
        // Error handling - suppress expiration errors during auto-send
        // For session expiration during auto-send, don't show error or reset flag
        // User will see the error when they actually try to use the code
        if (!isExpirationError(err)) {
          // For non-expiration errors, show error and allow retry
          updateSnack({
            severity: 'error',
            message: err?.message || 'Failed to send verification code',
          });
          hasAutoSentRef.current = false;
        }
        // For session expiration, keep hasAutoSentRef.current = true to prevent infinite loop
        // Don't show error message - user hasn't done anything yet
        setLoadingStep(null);
      });
    }
    // Only depend on values that should trigger re-evaluation, not functions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaUser, mfa.hasPreferredMethod, mfa.preferredMethod, loadingStep]);

  const handleSelectDifferentMethod = () => {
    // Simply navigate to method selection screen
    // User can choose a different method for this login session
    setStep('mfa-method');
  };

  return {
    mfa,
    loadingStep,
    handleMfaMethodContinue,
    handleOtpSubmit,
    handleResendOtp,
    handleSelectDifferentMethod,
  };
}
